import { PermissionScopePicker } from "@/components/ApiTokens/PermissionScopePicker"
import {
  IconBox,
  VisionButton,
  VisionCard,
  VisionInput,
} from "@/components/VisionUI"
import {
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import { isAllOrgs, useOrganization } from "@/contexts/OrganizationContext"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import {
  type ApiTokenCreate,
  type ApiTokenCreateResponse,
  type ApiTokenPublic,
  type ApiTokenScope,
  ApiTokensService,
  PermissionFlag,
} from "@/lib/apiTokens"
import {
  Badge,
  Box,
  Code,
  Flex,
  HStack,
  Heading,
  Spinner,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FiAlertTriangle,
  FiCopy,
  FiKey,
  FiPlus,
  FiTrash2,
} from "react-icons/fi"

export const Route = createFileRoute("/_layout/api-tokens")({
  component: ApiTokensPage,
})

const EXPIRATION_OPTIONS: { value: number | null; key: string }[] = [
  { value: 30, key: "30d" },
  { value: 90, key: "90d" },
  { value: 365, key: "1y" },
  { value: null, key: "never" },
]

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return "—"
  return new Date(iso).toLocaleString()
}

function ScopeBadges({ scopes }: { scopes: ApiTokenScope[] }) {
  if (!scopes.length) return <Text fontSize="xs">—</Text>
  return (
    <Flex gap={1} wrap="wrap">
      {scopes.map((s, i) => {
        // Render a compact list of flag short-codes (R/C/U/D)
        const flags: string[] = []
        if (s.permission & PermissionFlag.READ) flags.push("R")
        if (s.permission & PermissionFlag.CREATE) flags.push("C")
        if (s.permission & PermissionFlag.UPDATE) flags.push("U")
        if (s.permission & PermissionFlag.DELETE) flags.push("D")
        return (
          <Badge key={`${s.resource_type}-${i}`} colorPalette="purple">
            {s.resource_type}: {flags.join("+") || "none"}
          </Badge>
        )
      })}
    </Flex>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Just-created banner — shows the plaintext token exactly once
// ────────────────────────────────────────────────────────────────────────────

function PlaintextBanner({
  created,
  onDismiss,
}: {
  created: ApiTokenCreateResponse
  onDismiss: () => void
}) {
  const { t } = useTranslation("apiTokens")
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    await navigator.clipboard.writeText(created.token)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Box
      borderRadius="lg"
      borderWidth="1px"
      borderColor="orange.400"
      bg="orange.50"
      _dark={{ bg: "orange.900", borderColor: "orange.500" }}
      p={5}
      mb={6}
    >
      <Flex gap={3} align="flex-start">
        <FiAlertTriangle size={24} color="orange" />
        <Box flex="1">
          <Heading size="sm" mb={2}>
            {t("created.title")}
          </Heading>
          <Text fontSize="sm" mb={3}>
            {t("created.warning")}
          </Text>
          <HStack gap={2} mb={3}>
            <Code
              p={2}
              borderRadius="md"
              fontSize="sm"
              wordBreak="break-all"
              flex="1"
            >
              {created.token}
            </Code>
            <VisionButton variant="brand" size="sm" onClick={copy}>
              <FiCopy />
              {copied ? t("created.copied") : t("created.copy")}
            </VisionButton>
          </HStack>
          <Flex justify="flex-end">
            <VisionButton variant="ghost" size="sm" onClick={onDismiss}>
              {t("created.dismiss")}
            </VisionButton>
          </Flex>
        </Box>
      </Flex>
    </Box>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Create modal
// ────────────────────────────────────────────────────────────────────────────

function CreateTokenDialog({
  open,
  organisationId,
  onClose,
  onCreated,
}: {
  open: boolean
  organisationId: string | null
  onClose: () => void
  onCreated: (created: ApiTokenCreateResponse) => void
}) {
  const { t } = useTranslation("apiTokens")
  const [name, setName] = useState("")
  const [expiresInDays, setExpiresInDays] = useState<number | null>(90)
  const [scopes, setScopes] = useState<ApiTokenScope[]>([])
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setName("")
    setExpiresInDays(90)
    setScopes([])
    setError(null)
  }

  const createMut = useMutation({
    mutationFn: async (payload: ApiTokenCreate) =>
      ApiTokensService.createApiToken({ requestBody: payload }),
    onSuccess: (data) => {
      onCreated(data)
      reset()
      onClose()
    },
    onError: (e: any) => {
      setError(e?.body?.detail ?? e?.message ?? t("create.errorGeneric"))
    },
  })

  const handleSubmit = () => {
    setError(null)
    if (!name.trim()) {
      setError(t("create.nameRequired"))
      return
    }
    if (!organisationId) {
      setError(t("create.orgRequired"))
      return
    }
    if (!scopes.length) {
      setError(t("create.scopeRequired"))
      return
    }
    createMut.mutate({
      name: name.trim(),
      organisation_id: organisationId,
      expires_in_days: expiresInDays,
      scopes,
    })
  }

  return (
    <DialogRoot
      open={open}
      onExitComplete={reset}
      size={{ base: "sm", md: "xl" }}
      placement="center"
    >
      <DialogBackdrop />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("create.title")}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap={4}>
            <Field label={t("create.nameLabel")}>
              <VisionInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("create.namePlaceholder")}
              />
            </Field>
            <Field label={t("create.expirationLabel")}>
              <Flex gap={2} wrap="wrap">
                {EXPIRATION_OPTIONS.map((opt) => (
                  <VisionButton
                    key={opt.key}
                    type="button"
                    size="sm"
                    variant={expiresInDays === opt.value ? "brand" : "ghost"}
                    onClick={() => setExpiresInDays(opt.value)}
                  >
                    {t(`create.expiration.${opt.key}`)}
                  </VisionButton>
                ))}
              </Flex>
            </Field>
            <Field label={t("create.scopesLabel")}>
              <PermissionScopePicker value={scopes} onChange={setScopes} />
            </Field>
            {error && (
              <Text color="red.400" fontSize="sm">
                {error}
              </Text>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Flex gap={2} justify="flex-end" w="100%">
            <VisionButton
              variant="ghost"
              onClick={() => {
                reset()
                onClose()
              }}
              disabled={createMut.isPending}
            >
              {t("create.cancel")}
            </VisionButton>
            <VisionButton
              variant="brand"
              onClick={handleSubmit}
              loading={createMut.isPending}
            >
              <FiPlus />
              {t("create.submit")}
            </VisionButton>
          </Flex>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Confirm-revoke dialog
// ────────────────────────────────────────────────────────────────────────────

function RevokeDialog({
  token,
  onClose,
  onConfirmed,
}: {
  token: ApiTokenPublic | null
  onClose: () => void
  onConfirmed: (id: string) => void
}) {
  const { t } = useTranslation("apiTokens")
  return (
    <DialogRoot
      open={!!token}
      size={{ base: "sm", md: "md" }}
      placement="center"
    >
      <DialogBackdrop />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("revoke.title")}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text>{t("revoke.confirm", { name: token?.name ?? "" })}</Text>
          <Text fontSize="sm" color="red.400" mt={3}>
            {t("revoke.warning")}
          </Text>
        </DialogBody>
        <DialogFooter>
          <Flex gap={2} justify="flex-end" w="100%">
            <VisionButton variant="ghost" onClick={onClose}>
              {t("revoke.cancel")}
            </VisionButton>
            <VisionButton
              variant="brand"
              onClick={() => token && onConfirmed(token.id)}
            >
              <FiTrash2 />
              {t("revoke.submit")}
            </VisionButton>
          </Flex>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────────────

function ApiTokensPage() {
  const { t } = useTranslation("apiTokens")
  const sc = useSemanticColors()
  const { currentOrganization, isLoading: orgLoading } = useOrganization()
  const queryClient = useQueryClient()
  // The "All Departments" selector uses the sentinel ALL_ORGS_ID ("__all__"),
  // which is not a valid UUID — never send it to the backend. API tokens are
  // always scoped to a specific organisation.
  const organisationId =
    currentOrganization && !isAllOrgs(currentOrganization)
      ? currentOrganization.id
      : null

  const [createOpen, setCreateOpen] = useState(false)
  const [justCreated, setJustCreated] = useState<ApiTokenCreateResponse | null>(
    null,
  )
  const [revoking, setRevoking] = useState<ApiTokenPublic | null>(null)

  const tokensQuery = useQuery({
    queryKey: ["api-tokens", organisationId],
    queryFn: () =>
      ApiTokensService.listApiTokens({
        organisationId: organisationId ?? undefined,
      }),
    enabled: !!organisationId,
  })

  const revokeMut = useMutation({
    mutationFn: (id: string) =>
      ApiTokensService.revokeApiToken({ tokenId: id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["api-tokens", organisationId],
      })
      setRevoking(null)
    },
  })

  return (
    <Box>
      <Flex align="center" justify="space-between" mb={6}>
        <Box>
          <Heading size="xl" color={sc.heading} mb={2}>
            {t("title")}
          </Heading>
          <Text color={sc.textMuted}>{t("subtitle")}</Text>
        </Box>
        <VisionButton
          variant="brand"
          onClick={() => setCreateOpen(true)}
          disabled={!organisationId}
        >
          <FiPlus />
          {t("createButton")}
        </VisionButton>
      </Flex>

      {justCreated && (
        <PlaintextBanner
          created={justCreated}
          onDismiss={() => setJustCreated(null)}
        />
      )}

      {orgLoading || tokensQuery.isLoading ? (
        <Flex justify="center" py={12}>
          <Spinner size="lg" />
        </Flex>
      ) : !organisationId ? (
        <VisionCard p={8}>
          <Text color={sc.textMuted} textAlign="center">
            {t("noOrgSelected")}
          </Text>
        </VisionCard>
      ) : tokensQuery.data && tokensQuery.data.count === 0 ? (
        <VisionCard p={8}>
          <Flex direction="column" align="center" gap={3} py={6}>
            <IconBox variant="glass" size="lg">
              <FiKey size={28} color={sc.iconColor} />
            </IconBox>
            <Text color={sc.textMuted}>{t("emptyState")}</Text>
          </Flex>
        </VisionCard>
      ) : (
        <VisionCard p={0} overflow="hidden">
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>{t("table.name")}</Table.ColumnHeader>
                <Table.ColumnHeader>{t("table.prefix")}</Table.ColumnHeader>
                <Table.ColumnHeader>{t("table.scopes")}</Table.ColumnHeader>
                <Table.ColumnHeader>{t("table.created")}</Table.ColumnHeader>
                <Table.ColumnHeader>{t("table.expires")}</Table.ColumnHeader>
                <Table.ColumnHeader>{t("table.lastUsed")}</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">
                  {t("table.actions")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {tokensQuery.data?.data.map((tok) => (
                <Table.Row key={tok.id}>
                  <Table.Cell fontWeight="600">{tok.name}</Table.Cell>
                  <Table.Cell>
                    <Code fontSize="xs">{tok.token_prefix}…</Code>
                  </Table.Cell>
                  <Table.Cell>
                    <ScopeBadges scopes={tok.scopes} />
                  </Table.Cell>
                  <Table.Cell fontSize="sm">
                    {formatDate(tok.created_at)}
                  </Table.Cell>
                  <Table.Cell fontSize="sm">
                    {tok.expires_at
                      ? formatDate(tok.expires_at)
                      : t("table.never")}
                  </Table.Cell>
                  <Table.Cell fontSize="sm">
                    {formatDate(tok.last_used_at)}
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <VisionButton
                      size="xs"
                      variant="ghost"
                      color="red.400"
                      onClick={() => setRevoking(tok)}
                    >
                      <FiTrash2 />
                      {t("table.revoke")}
                    </VisionButton>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </VisionCard>
      )}

      <CreateTokenDialog
        open={createOpen}
        organisationId={organisationId}
        onClose={() => setCreateOpen(false)}
        onCreated={setJustCreated}
      />
      <RevokeDialog
        token={revoking}
        onClose={() => setRevoking(null)}
        onConfirmed={(id) => revokeMut.mutate(id)}
      />
    </Box>
  )
}
